/**
 * 1.7.9 — only what applies (D-1.7.9-3, D-1.7.9-7).
 *
 * One function decides what an event shows, for the event's page, for the Month view's expanded card and for New
 * event's second step. An event with RSVP off shows nothing about RSVP — no counts, places, draw, guests or invite;
 * an event that needs no volunteers has no Volunteers card (17 Oct 2026: unless a volunteer holds a place anyway, so
 * nobody is hidden); an event where nothing is served has no delivery line.
 *
 * Pure: nothing here reads or writes a list, and nothing here hides a record — an answer given before RSVP was
 * turned off is kept and shown under its own heading (`oldAnswers`), never deleted.
 */
import type { EventView } from './events';

/* ------------------------------------------------------------------------------------------------
 * The shape of an event
 * ---------------------------------------------------------------------------------------------- */

/** What decides the cards: three switches, and where the event stands. */
export interface EventShape {
  /** People answer (RSVP on), or it is on the calendar only. */
  rsvp: boolean;
  /** Volunteers needed (0 = none asked for). */
  volunteersNeeded: number;
  /**
   * 17 Oct 2026: volunteers (in people) who hold a place while the event's number is empty — "no volunteers needed",
   * yet invited or added anyway. They are never hidden: the Volunteers card stays and says "How many? Not set".
   * Absent: nobody.
   */
  volunteersWithoutNumber?: number;
  /** Food or items served. */
  served: boolean;
  /** The places are given by a raffle. */
  raffle: boolean;
  /** Tenant and guest lines that hold an answer (yes or no) — what "Answers before RSVP was turned off" lists. */
  answers: number;
  cancelled: boolean;
  past: boolean;
}

/** Everything an event's page (or its card on the Month view) can show. */
export const EVENT_SECTIONS = [
  'details',
  'edit',
  'reminders',
  'announce',
  'volunteers',
  // — RSVP —
  'rsvpCounts',
  'openLink',
  'invite',
  'review',
  'answers',
  'numbers',
  'guests',
  'draw',
  'addByHand',
  'checkIn',
  'charts',
  'prints',
  'served',
  'delivery',
  // — kept, not asked for any more —
  'oldAnswers',
] as const;
export type EventSection = (typeof EVENT_SECTIONS)[number];

/** Every section that is about RSVP: none of them shows on an event with RSVP off. */
export const RSVP_SECTIONS: readonly EventSection[] = ['rsvpCounts', 'openLink', 'invite', 'review', 'answers', 'numbers', 'guests', 'draw', 'addByHand', 'checkIn', 'charts', 'prints', 'served', 'delivery'];

export interface CardViewer {
  /** The administrator or a co-administrator (the ones who may change an event). */
  canWrite: boolean;
}

/**
 * What shows. The one rule for both views:
 *
 *   RSVP off            nothing about RSVP at all (`RSVP_SECTIONS`); answers given earlier stay under `oldAnswers`
 *   Volunteers needed 0 no Volunteers card — 17 Oct 2026: unless the number is empty and a volunteer holds a place
 *                       anyway; then the card stays ("How many? Not set") so nobody is hidden. Nobody more is asked.
 *   Nothing served      no delivery line
 *   Not a raffle        no draw
 *   Edit, Announce      the administrator and a co-administrator, on an event that is neither cancelled nor past
 */
export function eventSections(shape: EventShape, viewer: CardViewer): ReadonlySet<EventSection> {
  const out = new Set<EventSection>(['details', 'reminders']);
  const open = !shape.cancelled && !shape.past;
  if (viewer.canWrite && open) {
    out.add('edit');
    out.add('announce');
  }
  if (shape.volunteersNeeded > 0 || (shape.volunteersWithoutNumber ?? 0) > 0) out.add('volunteers');
  if (shape.rsvp) {
    for (const s of RSVP_SECTIONS) out.add(s);
    if (!shape.raffle) out.delete('draw');
    if (!shape.served) out.delete('delivery');
  } else if (shape.answers > 0) out.add('oldAnswers');
  return out;
}

export const showsSection = (shape: EventShape, viewer: CardViewer, section: EventSection) => eventSections(shape, viewer).has(section);

/**
 * 18 Oct 2026 (D-1018-EV, the 17 Oct fault): Check-in is offered only on the event's own day — the Events list's rule
 * (1.8.4), now the one rule for the event's page too. `todayKey` is the service day (yyyyMMdd, America/New_York) and
 * `dayKey` the Events list's own date; the administrator and a co-administrator check people in, on an event with RSVP
 * that is not cancelled.
 */
export function checkInOffered(e: Pick<EventView, 'cal' | 'numbers' | 'served' | 'rule' | 'counts' | 'status' | 'past' | 'dayKey'> & Partial<Pick<EventView, 'three'>>, todayKey: string, viewer: CardViewer): boolean {
  if (!viewer.canWrite || e.status === 'Cancelled' || e.dayKey !== todayKey) return false;
  return eventSections(shapeOfEvent(e), viewer).has('checkIn');
}

/**
 * 1.8.3 (D-1.8.3-6) — one press for volunteers on an event's page, never two.
 *
 *   Volunteers needed above 0   `request` — "Request volunteers for this event", at the top of the Volunteers card
 *                               (the 1.7.7 request: the number, the cap, the standby list). The 1.7.4 "Invite
 *                               volunteers" is not shown beside it; its server path stays for the links already sent.
 *   No number, RSVP on, and     `invite` — the 1.7.4 Invite volunteers in the Invite card, exactly as before (an
 *   volunteers in the audience  event volunteers are welcome at, with nobody asked to help).
 *   Otherwise                   nothing.
 */
export type VolunteerPress = 'request' | 'invite';
export function volunteerPress(shape: Pick<EventShape, 'rsvp' | 'volunteersNeeded'>, volunteersInAudience: boolean): VolunteerPress | null {
  if (shape.volunteersNeeded > 0) return 'request';
  return shape.rsvp && volunteersInAudience ? 'invite' : null;
}

/** The shape of an event as the server sends it. */
export function shapeOfEvent(e: Pick<EventView, 'cal' | 'numbers' | 'served' | 'rule' | 'counts' | 'status' | 'past'> & Partial<Pick<EventView, 'three'>>): EventShape {
  // 17 Oct 2026: an empty number is "no volunteers needed" — but whoever holds a volunteer place anyway is still shown
  const withoutNumber = e.numbers.volunteerPlaces === null ? (e.three?.places.volunteersGoing ?? 0) : 0;
  return {
    rsvp: e.cal.rsvp,
    volunteersNeeded: e.numbers.volunteerPlaces ?? 0,
    ...(withoutNumber > 0 ? { volunteersWithoutNumber: withoutNumber } : {}),
    served: e.served.on,
    raffle: e.rule === 'raffle',
    answers: e.counts.saidYes + e.counts.no,
    cancelled: e.status === 'Cancelled',
    past: e.past,
  };
}

/* ------------------------------------------------------------------------------------------------
 * New event: the kind first, then only its fields
 * ---------------------------------------------------------------------------------------------- */

/** Step 1's one question. "Needs volunteers" can be combined with either answer. */
export interface EventKindChoice {
  /** On the calendar only (bingo, music, the monthly Tech Discussion — tenants only, no answer needed) · People answer — RSVP. */
  answer: 'calendar' | 'rsvp';
  volunteers: boolean;
}
export const CALENDAR_ONLY: EventKindChoice = { answer: 'calendar', volunteers: false };

/** Every field New event (and Edit) can ask for. */
export const EVENT_FIELDS = [
  // every event
  'name',
  'kind',
  'day',
  'start',
  'end',
  'place',
  'description',
  'bring',
  'access',
  'repeat',
  'reminders',
  // People answer — RSVP
  'rule',
  'capacity',
  'tickets',
  'maxPerHousehold',
  'audience',
  'guestsAllowed',
  'rsvpClose',
  'householdExtra',
  'served',
  'servedWhat',
  // Needs volunteers
  'volunteersNeeded',
] as const;
export type EventField = (typeof EVENT_FIELDS)[number];

const ALWAYS: readonly EventField[] = ['name', 'kind', 'day', 'start', 'end', 'place', 'description', 'bring', 'access', 'repeat', 'reminders'];
/** The fields only an event with RSVP asks for. */
export const RSVP_FIELDS_179: readonly EventField[] = ['rule', 'capacity', 'tickets', 'maxPerHousehold', 'audience', 'guestsAllowed', 'rsvpClose', 'householdExtra', 'served', 'servedWhat'];

/** Step 2: only the fields that kind needs. */
export function eventFields(choice: EventKindChoice): ReadonlySet<EventField> {
  const out = new Set<EventField>(ALWAYS);
  if (choice.answer === 'rsvp') for (const f of RSVP_FIELDS_179) out.add(f);
  if (choice.volunteers) out.add('volunteersNeeded');
  return out;
}

/** The kind an event is now (Edit opens on it; it can be changed there). */
export const kindChoiceOf = (shape: Pick<EventShape, 'rsvp' | 'volunteersNeeded'>): EventKindChoice => ({ answer: shape.rsvp ? 'rsvp' : 'calendar', volunteers: shape.volunteersNeeded > 0 });

/**
 * What the kind fixes on the save, whatever the hidden fields held: a calendar-only event is for tenants, takes
 * everyone, has no room count, no guests, nothing served; an event that needs no volunteers asks for none.
 */
export function kindDefaults(choice: EventKindChoice): { rsvp: boolean; rule?: 'everyone'; capacity?: null; tickets?: null; audience?: { tenants: true; volunteers: 'none' }; served?: false; servedWhat?: ''; rsvpCloseDayKey?: null; householdExtra?: false; volunteersNeeded?: 0 } {
  return {
    ...(choice.answer === 'calendar' ? { rsvp: false, rule: 'everyone', capacity: null, tickets: null, audience: { tenants: true, volunteers: 'none' }, served: false, servedWhat: '', rsvpCloseDayKey: null, householdExtra: false } : { rsvp: true }),
    ...(choice.volunteers ? {} : { volunteersNeeded: 0 }),
  };
}

/* ------------------------------------------------------------------------------------------------
 * The Month view's collapsed row: day · time · title · place · one status word
 * ---------------------------------------------------------------------------------------------- */

export const STATUS_WORDS = ['cancelled', 'past', 'today', 'rsvpOpen', 'rsvpClosed', 'calendar'] as const;
/** One word a row; the screens turn it into English or Spanish (with an icon — never colour alone). */
export type StatusWord = (typeof STATUS_WORDS)[number];

export function statusWord(e: Pick<EventView, 'status' | 'past' | 'dayKey' | 'cal'>, todayKey: string): StatusWord {
  if (e.status === 'Cancelled') return 'cancelled';
  if (e.past || e.dayKey < todayKey) return 'past';
  if (e.dayKey === todayKey) return 'today';
  if (!e.cal.rsvp) return 'calendar';
  return e.status === 'RSVPs Closed' || e.status === 'Raffle Drawn' || e.status === 'Completed' ? 'rsvpClosed' : 'rsvpOpen';
}
